/**
 * Group Portal - Interactive Safari Group Management
 * Matches Tour Details UI with enhanced group features
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { ClientAuthService } from '../services/clientAuthService';
import { groupService, Group, GroupMember } from '../services/groupService';
import { chatService, ChatMessage } from '../services/chatService';
import {
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TruckIcon,
  UserIcon,
  CameraIcon,
  StarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  HeartIcon,
  ShareIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  ChatBubbleOvalLeftIcon
} from '@heroicons/react/24/outline';

// Local interface for UI-specific GroupMember properties
interface UIGroupMember {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  isAnonymous: boolean;
  joinedAt: string;
  isOnline: boolean;
  isAdmin?: boolean;
  isGuide?: boolean;
}

// Local ChatMessage interface for UI compatibility
interface UIChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
  isAdmin: boolean;
  isGuide: boolean;
  type: 'text' | 'image' | 'file' | 'system' | 'update';
}

interface GroupInfo {
  id: string;
  name: string;
  tourName: string;
  description: string;
  duration: string;
  startDate: string;
  endDate: string;
  status: 'preparing' | 'active' | 'completed' | 'cancelled';
  members: UIGroupMember[];
  guide?: {
    name: string;
    photo: string;
    experience: string;
    specialties: string[];
    phone: string;
    email: string;
    introduction?: string;
    isIntroduced: boolean;
  };
  vehicle?: {
    registration: string;
    type: string;
    capacity: number;
    features: string[];
    images: string[];
    driver: string;
    driverPhone: string;
    specifications: string[];
  };
  accommodation?: {
    name: string;
    type: string;
    location: string;
    images: string[];
    features: string[];
    contact: string;
    phone: string;
    amenities: string[];
  };
  itinerary: Array<{
    day: number;
    title: string;
    description: string;
    activities: string[];
    meals: string[];
    accommodation: string;
    isCompleted: boolean;
    startTime?: string;
    endTime?: string;
  }>;
  countdown: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  updates: Array<{
    id: string;
    title: string;
    message: string;
    timestamp: string;
    type: 'info' | 'warning' | 'success' | 'swap';
    isRead: boolean;
  }>;
  reviews: Array<{
    id: string;
    author: string;
    rating: number;
    comment: string;
    timestamp: string;
  }>;
}

const GroupPortal: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [chatMessages, setChatMessages] = useState<UIChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAnonymousMembers, setShowAnonymousMembers] = useState(false);
  const [currentClient, setCurrentClient] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fallback data function for when booking data is not found
  const getFallbackGroupData = (groupId: string): GroupInfo => {
    return {
      id: groupId,
      name: 'Safari Group',
      tourName: 'Maasai Mara Safari',
      description: 'Experience the Great Migration and witness the Big Five in their natural habitat. This safari takes you through the most spectacular wildlife viewing areas.',
      duration: '4 days / 3 nights',
      startDate: '2024-02-15',
      endDate: '2024-02-18',
      status: 'preparing',
      members: [
        {
          id: 'member_001',
          name: 'John Doe',
          email: 'john@example.com',
          isAnonymous: false,
          joinedAt: '2024-01-15T10:00:00Z',
          isOnline: true
        },
        {
          id: 'admin_001',
          name: 'SightTrails Admin',
          email: 'admin@sighttrails.com',
          isAnonymous: false,
          joinedAt: '2024-01-15T10:00:00Z',
          isOnline: true,
          isAdmin: true
        }
      ],
      guide: {
        name: 'Peter Kimani',
        photo: '/images/default-guide.jpg',
        experience: '8 years',
        specialties: ['Wildlife Photography', 'Bird Watching', 'Cultural Tours'],
        phone: '+254 700 000 100',
        email: 'peter@sighttrails.com',
        introduction: 'Meet Peter, your experienced safari guide with over 8 years of experience leading wildlife tours across Kenya.',
        isIntroduced: false
      },
      vehicle: {
        registration: 'KDA 123A',
        type: 'Toyota Land Cruiser',
        capacity: 7,
        features: ['4WD', 'Pop-up Roof', 'Refrigerator', 'First Aid Kit', 'GPS Navigation'],
        images: ['/images/vehicle-1.jpg', '/images/vehicle-2.jpg'],
        driver: 'John Mwangi',
        driverPhone: '+254 700 000 200',
        specifications: [
          'Engine: 4.5L V8 Diesel',
          'Transmission: Automatic',
          'Seating: 7 passengers',
          'Air Conditioning: Yes',
          'Safety: ABS, Airbags, Roll Cage'
        ]
      },
      accommodation: {
        name: 'Mara Serena Lodge',
        type: 'Luxury Lodge',
        location: 'Maasai Mara National Reserve',
        images: ['/images/lodge-1.jpg', '/images/lodge-2.jpg'],
        features: ['Swimming Pool', 'Restaurant', 'Bar', 'WiFi', 'Spa'],
        contact: 'Mara Serena Lodge',
        phone: '+254 20 284 2000',
        amenities: ['Air Conditioning', 'Private Bathroom', 'Balcony', 'Room Service']
      },
      itinerary: [
        {
          day: 1,
          title: 'Arrival and Game Drive',
          description: 'Arrive at the lodge and enjoy your first game drive in the Maasai Mara.',
          activities: ['Airport Transfer', 'Lunch', 'Evening Game Drive', 'Dinner'],
          meals: ['Lunch', 'Dinner'],
          accommodation: 'Mara Serena Lodge',
          isCompleted: false
        },
        {
          day: 2,
          title: 'Full Day Game Drive',
          description: 'Spend the entire day exploring the vast plains of the Maasai Mara.',
          activities: ['Early Morning Game Drive', 'Breakfast in the Bush', 'Full Day Game Drive', 'Picnic Lunch'],
          meals: ['Breakfast', 'Lunch', 'Dinner'],
          accommodation: 'Mara Serena Lodge',
          isCompleted: false
        }
      ],
      updates: [
        {
          id: 'update_001',
          title: 'Group Created',
          message: 'Your safari group has been created and is ready for the adventure!',
          timestamp: '2024-01-15T10:00:00Z',
          type: 'success' as const,
          isRead: false
        },
        {
          id: 'update_002',
          title: 'Guide Assigned',
          message: 'Peter Kimani has been assigned as your safari guide.',
          timestamp: '2024-01-16T14:30:00Z',
          type: 'info' as const,
          isRead: false
        }
      ],
      reviews: [
        {
          id: 'review_001',
          author: 'John Doe',
          rating: 5,
          comment: 'Amazing safari experience with excellent guide and comfortable accommodation.',
          timestamp: '2024-01-20T15:30:00Z'
        }
      ],
      countdown: {
        days: 15,
        hours: 8,
        minutes: 30,
        seconds: 45
      }
    };
  };

  // Check authentication on mount
  useEffect(() => {
    const client = ClientAuthService.getCurrentClient();
    const hasAccess = ClientAuthService.hasPortalAccess();

    if (!hasAccess || !client) {
      console.warn('No client access, redirecting to home');
      navigate('/');
      return;
    }

    // Check if groupId is provided in URL
    if (!groupId) {
      console.error('No group ID in URL');
      setError('No group ID provided. Please access the portal through your booking confirmation link.');
      setLoading(false);
      return;
    }

    setCurrentClient(client);
    setIsAuthenticated(true);
    loadGroupData();
  }, [groupId, navigate]);

  const loadGroupData = async () => {
    try {
      // Clear any previous errors
      setError(null);

      if (!groupId) {
        console.error('No group ID provided');
        setError('No group ID provided. Please check your booking confirmation.');
        setLoading(false);
        return;
      }

      // Validate UUID format before querying
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(groupId)) {
        // This is expected for old bookings - don't spam console
        // Only log once per session
        if (!sessionStorage.getItem('group_id_warn_shown')) {
          console.warn('Invalid group ID format (not UUID). This is expected for old bookings:', groupId);
          sessionStorage.setItem('group_id_warn_shown', 'true');
        }
        setError('Invalid group ID format. This may be due to an old booking. Please contact support with your booking reference, or try logging in again.');
        setLoading(false);
        return;
      }

      // Fetch group data from the new group service
      const group = await groupService.getGroupById(groupId);

      if (!group) {
        console.warn('Group not found:', groupId);
        setError('Group not found. This may be because the group hasn\'t been created yet or the booking is still being processed. Please contact support if this issue persists.');
        setLoading(false);
        return;
      }

      // Get group members
      const members = await groupService.getGroupMembers(groupId);

      // Transform group data into GroupInfo format
      const groupInfo: GroupInfo = {
        id: group.id,
        name: group.name,
        tourName: group.tourName,
        description: group.description,
        duration: group.duration,
        startDate: group.startDate,
        endDate: group.endDate,
        status: group.status,
        members: [
          // Group members
          ...members.map((member: GroupMember) => ({
            id: member.id,
            name: member.name,
            email: member.email,
            isAnonymous: false,
            joinedAt: member.joinedAt,
            isOnline: false
          })),
          // Admin member
          {
            id: 'admin_001',
            name: 'SightTrails Admin',
            email: 'admin@sighttrails.com',
            isAnonymous: false,
            joinedAt: group.createdAt,
            isOnline: true,
            isAdmin: true
          }
        ],
        guide: group.guide ? {
          name: group.guide.name,
          photo: group.guide.avatar || '/images/default-guide.jpg',
          experience: group.guide.experience,
          specialties: group.guide.languages || ['Wildlife', 'Photography'],
          phone: '+254 708 560 665',
          email: 'guide@sighttrails.com',
          introduction: `Meet your experienced guide ${group.guide.name}, who has been leading safaris for over ${group.guide.experience} years.`,
          isIntroduced: false
        } : undefined,
        vehicle: group.vehicle ? {
          registration: group.vehicle.registration,
          type: group.vehicle.type,
          capacity: group.vehicle.capacity,
          features: ['4WD', 'Pop-up Roof', 'Refrigerator', 'First Aid Kit', 'GPS Navigation'],
          images: ['/images/vehicle-1.jpg', '/images/vehicle-2.jpg'],
          driver: group.vehicle.driver,
          driverPhone: '+254 700 000 200',
          specifications: [
            'Engine: 4.5L V8 Diesel',
            'Transmission: Automatic',
            `Seating: ${group.vehicle.capacity} passengers`,
            'Air Conditioning: Yes',
            'Safety: ABS, Airbags, Roll Cage'
          ]
        } : {
          registration: 'To be assigned',
          type: 'Safari Vehicle',
          capacity: group.maxCapacity,
          features: ['4WD', 'Pop-up Roof', 'Refrigerator', 'First Aid Kit', 'GPS Navigation'],
          images: ['/images/vehicle-1.jpg', '/images/vehicle-2.jpg'],
          driver: 'To be assigned',
          driverPhone: '+254 700 000 200',
          specifications: [
            'Engine: 4.5L V8 Diesel',
            'Transmission: Automatic',
            `Seating: ${group.maxCapacity} passengers`,
            'Air Conditioning: Yes',
            'Safety: ABS, Airbags, Roll Cage'
          ]
        },
        accommodation: group.accommodation ? {
          name: group.accommodation.name,
          type: group.accommodation.type,
          location: group.accommodation.location,
          images: ['/images/lodge-1.jpg', '/images/lodge-2.jpg'],
          features: group.accommodation.amenities,
          contact: group.accommodation.contact,
          phone: group.accommodation.phone,
          amenities: group.accommodation.amenities
        } : {
          name: 'To be assigned',
          type: 'Lodge',
          location: 'To be confirmed',
          images: ['/images/lodge-1.jpg', '/images/lodge-2.jpg'],
          features: ['Restaurant', 'Bar', 'Swimming Pool', 'WiFi'],
          contact: 'Reservations Manager',
          phone: '+254 700 000 300',
          amenities: [
            '24/7 Room Service',
            'Laundry Service',
            'Currency Exchange',
            'Gift Shop'
          ]
        },
        itinerary: group.itinerary && group.itinerary.length > 0 ? group.itinerary.map((item: any, index: number) => ({
          day: item.day || index + 1,
          title: item.title || `Day ${index + 1}`,
          description: item.description || item.activities?.join(', ') || 'Safari activities',
          activities: item.activities || [],
          meals: item.meals || ['Breakfast', 'Lunch', 'Dinner'],
          accommodation: item.accommodation || 'Lodge accommodation',
          isCompleted: item.isCompleted || false,
          startTime: item.startTime || '08:00',
          endTime: item.endTime || '18:00'
        })) : [
          {
            day: 1,
            title: 'Safari Day 1',
            description: group.description || 'Amazing safari experience',
            activities: ['Game drive', 'Wildlife viewing'],
            meals: ['Breakfast', 'Lunch', 'Dinner'],
            accommodation: 'Lodge accommodation',
            isCompleted: false,
            startTime: '08:00',
            endTime: '18:00'
          }
        ],
        countdown: {
          days: Math.max(0, Math.floor((new Date(group.startDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))),
          hours: Math.max(0, Math.floor(((new Date(group.startDate).getTime() - new Date().getTime()) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))),
          minutes: Math.max(0, Math.floor(((new Date(group.startDate).getTime() - new Date().getTime()) % (1000 * 60 * 60)) / (1000 * 60))),
          seconds: Math.max(0, Math.floor(((new Date(group.startDate).getTime() - new Date().getTime()) % (1000 * 60)) / 1000))
        },
        updates: [
          {
            id: 'update_001',
            title: 'Booking Confirmed',
            message: `Your booking for ${group.tourName} has been confirmed. Welcome to your safari group!`,
            timestamp: group.createdAt,
            type: 'success',
            isRead: true
          },
          {
            id: 'update_002',
            title: 'Tour Details',
            message: `Your ${group.duration || 'safari'} will start on ${new Date(group.startDate).toLocaleDateString()}.`,
            timestamp: group.createdAt,
            type: 'info',
            isRead: true
          }
        ],
        reviews: []
      };

      const initialChatMessages: UIChatMessage[] = [
        {
          id: 'msg_001',
          senderId: 'admin_001',
          senderName: 'SightTrails Admin',
          message: `Welcome to your ${group.tourName} group! We're excited to have you join us for this amazing safari adventure. Please feel free to ask any questions.`,
          timestamp: group.createdAt,
          isAdmin: true,
          isGuide: false,
          type: 'text'
        },
        {
          id: 'msg_002',
          senderId: 'member_001',
          senderName: 'John Doe',
          message: 'Thank you! I\'m really looking forward to this safari. What should I pack for the trip?',
          timestamp: '2024-01-20T09:15:00Z',
          isAdmin: false,
          isGuide: false,
          type: 'text'
        },
        {
          id: 'msg_003',
          senderId: 'admin_001',
          senderName: 'SightTrails Admin',
          message: 'Great question! We\'ll send you a detailed packing list via email. Essentials include: comfortable clothing, camera, binoculars, and sunscreen.',
          timestamp: '2024-01-20T09:20:00Z',
          isAdmin: true,
          isGuide: false,
          type: 'text'
        },
        {
          id: 'msg_004',
          senderId: 'admin_001',
          senderName: 'SightTrails Admin',
          message: '📸 Photography Workshop: We\'ll have a special photography session on Day 3. Bring your cameras!',
          timestamp: '2024-01-22T15:00:00Z',
          isAdmin: true,
          isGuide: false,
          type: 'update'
        }
      ];

      setGroupInfo(groupInfo);
      setChatMessages(initialChatMessages);
      setLoading(false);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Failed to load group data:', error);
      setError('Failed to load group data. Please try again or contact support.');
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !groupInfo?.id) return;

    const senderId = currentClient?.id || 'current_user';
    const senderName = currentClient?.isAnonymous ? 'Anonymous Traveler' : currentClient?.name || 'You';

    try {
      const sentMessage = await chatService.sendMessage(
        groupInfo.id,
        senderId,
        senderName,
        false, // isAdmin = false for clients
        newMessage
      );

      if (sentMessage) {
        // Convert to UI format
        const newMsg: UIChatMessage = {
          id: sentMessage.id,
          senderId: sentMessage.senderId,
          senderName: sentMessage.senderName,
          message: sentMessage.message,
          timestamp: sentMessage.createdAt,
          isAdmin: sentMessage.isAdmin,
          isGuide: false,
          type: sentMessage.messageType
        };

        setChatMessages(prev => [...prev, newMsg]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleWriteReview = () => {
    // Navigate to review form or open modal
    console.log('Write review clicked');
  };

  const toggleAnonymousMode = () => {
    ClientAuthService.toggleAnonymousMode();
    setCurrentClient(ClientAuthService.getCurrentClient());
  };

  const getUpdateIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'info': return <ClockIcon className="w-5 h-5 text-blue-500" />;
      case 'swap': return <ArrowRightIcon className="w-5 h-5 text-purple-500" />;
      default: return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getUpdateColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'info': return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'swap': return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      default: return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto mb-4"></div>
          <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
            }`}>Loading your safari group...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="text-center py-12 max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className={`w-16 h-16 mx-auto mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-yellow-500' : 'text-yellow-600'
            }`} />
          <h2 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>Unable to Load Group</h2>
          <p className={`mb-6 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
            }`}>{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                if (groupId) {
                  loadGroupData();
                } else {
                  navigate('/client-login');
                }
              }}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-300"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/client-login')}
              className="block w-full px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-800 dark:text-slate-200 rounded-lg transition-colors duration-300"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="text-center py-12">
          <ExclamationTriangleIcon className={`w-16 h-16 mx-auto mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-600' : 'text-gray-400'
            }`} />
          <h2 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>Access Denied</h2>
          <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
            }`}>You need to be authenticated to access this portal.</p>
        </div>
      </div>
    );
  }

  if (!groupInfo) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="text-center py-12">
          <ExclamationTriangleIcon className={`w-16 h-16 mx-auto mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-600' : 'text-gray-400'
            }`} />
          <h2 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>Group Not Found</h2>
          <p className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
            }`}>The group you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
      }`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Home
            </button>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleAnonymousMode}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover-magnify-sm ${currentClient?.isAnonymous
                    ? actualTheme === 'dark'
                      ? 'bg-slate-700 text-slate-300'
                      : 'bg-gray-200 text-gray-700'
                    : actualTheme === 'dark'
                      ? 'bg-blue-900/20 text-blue-300'
                      : 'bg-blue-100 text-blue-700'
                  }`}
              >
                {currentClient?.isAnonymous ? (
                  <EyeSlashIcon className="w-4 h-4 mr-1" />
                ) : (
                  <EyeIcon className="w-4 h-4 mr-1" />
                )}
                {currentClient?.isAnonymous ? 'Anonymous' : 'Visible'}
              </button>
            </div>
          </div>

          <div className={`backdrop-blur-md shadow-modern rounded-2xl p-8 border transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
              : 'bg-white/95 border-neutral-200/50'
            }`}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className={`text-4xl font-bold transition-colors duration-300 ${actualTheme === 'dark'
                    ? 'text-slate-200'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'
                  }`}>
                  {groupInfo.name}
                </h1>
                <p className={`mt-2 text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                  {groupInfo.tourName} • {groupInfo.duration}
                </p>
                <p className={`mt-1 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                  }`}>
                  {new Date(groupInfo.startDate).toLocaleDateString()} - {new Date(groupInfo.endDate).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                  {groupInfo.countdown.days}d {groupInfo.countdown.hours}h {groupInfo.countdown.minutes}m
                </div>
                <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                  Until Safari
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: UserGroupIcon },
              { id: 'itinerary', name: 'Itinerary', icon: CalendarDaysIcon },
              { id: 'logistics', name: 'Logistics', icon: TruckIcon },
              { id: 'chat', name: 'Group Chat', icon: ChatBubbleLeftRightIcon },
              { id: 'guide', name: 'Guide', icon: UserIcon }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-xl font-medium transition-all duration-300 hover-magnify-sm ${activeTab === tab.id
                      ? actualTheme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : actualTheme === 'dark'
                        ? 'text-slate-400 hover:text-slate-200'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Tour Description */}
                <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                    : 'bg-white/95 border-neutral-200/50'
                  }`}>
                  <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Safari Description</h3>
                  <p className={`leading-relaxed transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                    }`}>
                    {groupInfo.description}
                  </p>
                </div>

                {/* Group Members */}
                <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                    : 'bg-white/95 border-neutral-200/50'
                  }`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-xl font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Group Members ({groupInfo.members.length})</h3>
                    <button
                      onClick={() => setShowAnonymousMembers(!showAnonymousMembers)}
                      className={`text-sm transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-700'
                        }`}
                    >
                      {showAnonymousMembers ? 'Hide Anonymous' : 'Show All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groupInfo.members
                      .filter(member => !member.isAnonymous || showAnonymousMembers)
                      .map((member) => (
                        <div key={member.id} className={`flex items-center p-4 rounded-xl transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                          }`}>
                          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                            {member.isAnonymous ? '?' : member.name.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                              }`}>
                              {member.isAnonymous ? 'Anonymous Traveler' : member.name}
                              {member.isAdmin && <span className={`ml-2 text-xs px-2 py-1 rounded transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800'
                                }`}>Admin</span>}
                            </div>
                            <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                              }`}>
                              {member.isOnline ? 'Online' : 'Offline'}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Recent Updates */}
                <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                    : 'bg-white/95 border-neutral-200/50'
                  }`}>
                  <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Recent Updates</h3>
                  <div className="space-y-4">
                    {groupInfo.updates.map((update) => (
                      <div key={update.id} className={`p-4 rounded-xl border ${getUpdateColor(update.type)} ${!update.isRead ? 'ring-2 ring-blue-500' : ''}`}>
                        <div className="flex items-start">
                          {getUpdateIcon(update.type)}
                          <div className="ml-3 flex-1">
                            <h4 className="font-medium">{update.title}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{update.message}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {new Date(update.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'itinerary' && (
              <div className="space-y-6">
                {groupInfo.itinerary.map((day) => (
                  <div key={day.day} className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                      : 'bg-white/95 border-neutral-200/50'
                    }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className={`text-xl font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Day {day.day}: {day.title}</h3>
                        <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                          {day.startTime} - {day.endTime}
                        </p>
                      </div>
                      {day.isCompleted && (
                        <CheckCircleIcon className="w-6 h-6 text-green-500" />
                      )}
                    </div>
                    <p className={`mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                      }`}>{day.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Activities</h4>
                        <ul className="space-y-1">
                          {day.activities.map((activity, index) => (
                            <li key={index} className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                              }`}>
                              • {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Meals</h4>
                        <ul className="space-y-1">
                          {day.meals.map((meal, index) => (
                            <li key={index} className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                              }`}>
                              • {meal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {day.accommodation && (
                      <div className={`mt-4 p-3 rounded-xl transition-all duration-300 ${actualTheme === 'dark'
                          ? 'bg-slate-700/50 border border-slate-600/30'
                          : 'bg-gray-50 border border-gray-200/50'
                        }`}>
                        <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Accommodation: </span>
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                          }`}>{day.accommodation}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'logistics' && (
              <div className="space-y-6">
                {/* Vehicle */}
                {groupInfo.vehicle && (
                  <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                      : 'bg-white/95 border-neutral-200/50'
                    }`}>
                    <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Your Vehicle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>{groupInfo.vehicle.type}</h4>
                        <p className={`text-sm mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>
                          Registration: {groupInfo.vehicle.registration}
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <TruckIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                              }`} />
                            <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>Capacity: {groupInfo.vehicle.capacity} people</span>
                          </div>
                          <div className="flex items-center">
                            <UserIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                              }`} />
                            <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>Driver: {groupInfo.vehicle.driver}</span>
                          </div>
                          <div className="flex items-center">
                            <PhoneIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                              }`} />
                            <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>{groupInfo.vehicle.driverPhone}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Features</h4>
                        <ul className="space-y-1">
                          {groupInfo.vehicle.features.map((feature, index) => (
                            <li key={index} className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                              }`}>
                              • {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Accommodation */}
                {groupInfo.accommodation && (
                  <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                      : 'bg-white/95 border-neutral-200/50'
                    }`}>
                    <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Your Accommodation</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>{groupInfo.accommodation.name}</h4>
                        <p className={`text-sm mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>
                          {groupInfo.accommodation.type} • {groupInfo.accommodation.location}
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <PhoneIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                              }`} />
                            <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>{groupInfo.accommodation.phone}</span>
                          </div>
                          <div className="flex items-center">
                            <EnvelopeIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                              }`} />
                            <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                              }`}>{groupInfo.accommodation.contact}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Features</h4>
                        <ul className="space-y-1">
                          {groupInfo.accommodation.features.map((feature, index) => (
                            <li key={index} className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                              }`}>
                              • {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'chat' && (
              <div className={`backdrop-blur-md shadow-modern rounded-2xl border transition-all duration-300 flex flex-col h-96 ${actualTheme === 'dark'
                  ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                  : 'bg-white/95 border-neutral-200/50'
                }`}>
                <div className={`p-4 border-b transition-all duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-neutral-200/50'
                  }`}>
                  <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Group Chat</h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatMessages.map((message) => (
                    <div key={message.id} className={`flex ${message.senderId === currentClient?.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-xl ${message.senderId === currentClient?.id
                          ? 'bg-blue-500 text-white'
                          : message.isAdmin
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        }`}>
                        <div className="text-xs font-medium mb-1">
                          {message.senderName} {message.isAdmin && '(Admin)'}
                        </div>
                        <div className="text-sm">{message.message}</div>
                        <div className="text-xs opacity-75 mt-1">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 p-3 border rounded-xl focus:ring-2 focus:outline-none"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'guide' && groupInfo.guide && (
              <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                  ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                  : 'bg-white/95 border-neutral-200/50'
                }`}>
                <h3 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Your Guide</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className={`w-24 h-24 rounded-full mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`}></div>
                    <h4 className={`text-lg font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>{groupInfo.guide.name}</h4>
                    <p className={`text-sm mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                      }`}>
                      {groupInfo.guide.experience} years of experience
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <PhoneIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                          }`} />
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                          }`}>{groupInfo.guide.phone}</span>
                      </div>
                      <div className="flex items-center">
                        <EnvelopeIcon className={`w-5 h-5 mr-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                          }`} />
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                          }`}>{groupInfo.guide.email}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                      }`}>Specialties</h4>
                    <ul className="space-y-1">
                      {groupInfo.guide.specialties.map((specialty, index) => (
                        <li key={index} className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                          }`}>
                          • {specialty}
                        </li>
                      ))}
                    </ul>
                    {groupInfo.guide.introduction && (
                      <div className={`mt-4 p-3 rounded-xl transition-all duration-300 ${actualTheme === 'dark'
                          ? 'bg-blue-900/20 border border-blue-800/30'
                          : 'bg-blue-50 border border-blue-200/50'
                        }`}>
                        <h5 className={`font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>Guide Introduction</h5>
                        <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                          }`}>
                          {groupInfo.guide.introduction}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Countdown */}
            <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                : 'bg-white/95 border-neutral-200/50'
              }`}>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Safari Countdown</h3>
              <div className="text-center">
                <div className={`text-3xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                  {groupInfo.countdown.days}
                </div>
                <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>Days</div>
                <div className={`text-2xl font-bold transition-colors duration-300 mt-2 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                  {groupInfo.countdown.hours}
                </div>
                <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>Hours</div>
                <div className={`text-xl font-bold transition-colors duration-300 mt-2 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                  {groupInfo.countdown.minutes}
                </div>
                <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>Minutes</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className={`backdrop-blur-md shadow-modern rounded-2xl p-6 border transition-all duration-300 ${actualTheme === 'dark'
                ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-slate-600/30'
                : 'bg-white/95 border-neutral-200/50'
              }`}>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Quick Actions</h3>
              <div className="space-y-3">
                <button className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                    ? 'bg-blue-900/20 hover:bg-blue-900/30'
                    : 'bg-blue-50 hover:bg-blue-100'
                  }`}>
                  <PhoneIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-500'
                    }`} />
                  <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Contact Admin</span>
                </button>
                <button
                  onClick={handleWriteReview}
                  className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                      ? 'bg-green-900/20 hover:bg-green-900/30'
                      : 'bg-green-50 hover:bg-green-100'
                    }`}
                >
                  <StarIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-500'
                    }`} />
                  <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Write Review</span>
                </button>
                <button className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                    ? 'bg-purple-900/20 hover:bg-purple-900/30'
                    : 'bg-purple-50 hover:bg-purple-100'
                  }`}>
                  <ShareIcon className={`w-5 h-5 mr-3 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-purple-400' : 'text-purple-500'
                    }`} />
                  <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>Share Experience</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupPortal;